/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import { Node } from "@iventis/domain-model/model/node";
import { Content } from "@iventis/translations/content/typed-content";
import { useTreeBrowser } from "@iventis/tree-browser";
import React, { useMemo } from "react";
import { TreeBrowserDomainNodeContext } from "@iventis/tree-browser/implementations/library/helpers/library.helpers";
import { ConfirmationDialogComponent } from "@iventis/components";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import qs from "qs";
import { libraryApi } from "@iventis/api/src/api";
import { translate } from "@iventis/translations/translation";
import { Body1, styled } from "@iventis/styles";

export type ImportLayerStatus = "some" | "all" | "none";

export type ModalLocation = "library" | "map";

export const DeleteNodesConfirmationComponent: React.FC<{
    nodes: Node[];
    modalLocation: ModalLocation;
    onClose: (nodes?: Node[]) => void;
    onConfirm?: () => void;
    show: boolean;
}> = ({ show, nodes = [], modalLocation, onClose, onConfirm }) => {
    const [_, treeBrowserContext] = useTreeBrowser(TreeBrowserDomainNodeContext);

    const saveAndClose = async () => {
        treeBrowserContext.deleteNodes(nodes);

        const completeNodesRequest = await Promise.all(
            nodes.map((n) =>
                libraryApi.get(`/nodes/${n.id}/children`, {
                    params: { expandNodeType: [NodeType.Folder] },
                    paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
                })
            )
        );
        const completeNodes = completeNodesRequest.map((n) => n.data);
        onClose(completeNodes);
    };

    const text = useMemo(() => {
        if (nodes.length > 0) {
            return getDeleteConfirmationText(nodes, modalLocation);
        }
        return null;
    }, [nodes, modalLocation]);

    if (nodes == null || nodes.length === 0 || text == null) {
        return null;
    }

    return (
        <ConfirmationDialogComponent
            show={show}
            handleConfirm={onConfirm || saveAndClose}
            handleCancel={onClose}
            title={text.title}
            confirmText={translate(Content.common.buttons.delete)}
            cancelText={translate(Content.common.buttons.cancel)}
            isSubmitting={false}
        >
            <MessageContainer>
                <Body1>{text.description}</Body1>
                <Body1>{text.extraInfo}</Body1>
            </MessageContainer>
        </ConfirmationDialogComponent>
    );
};

const getDeleteConfirmationText = (nodes: Node[], modalLocation: ModalLocation): { title: string; description: string; extraInfo?: string } => {
    const isMultipleNodesSelected = nodes.length > 1;

    if (modalLocation === "library") {
        if (isMultipleNodesSelected) {
            return {
                title: translate(Content.library.delete_node.multi_title, {
                    count: nodes.length,
                }),
                description: translate(Content.library.delete_node.multi_description),
            };
        }
        const nodeType = nodes[0].type;
        return {
            title: translate(Content.library.delete_node.singular_title, {
                nodeType: `${translate(Content.library.node_names[nodeType])}`,
            }),
            description: translate(Content.library.delete_node.singular_description, {
                nodeType: `${translate(Content.library.node_names[nodeType])}`,
            }),
        };
    }

    const isGroupSelected = nodes.some((node) => node.type === NodeType.Folder);
    const importLayerStatus = checkIfNodesAreImported(nodes);

    if (isMultipleNodesSelected) {
        switch (true) {
            case isGroupSelected && importLayerStatus === "none":
                // Group is selected and no layers are imported
                return {
                    title: translate(Content.map7.delete.multipleTitleMix),
                    description: translate(Content.map7.delete.multipleDescriptionMix),
                };

            case isGroupSelected && importLayerStatus !== "none":
                // Group is selected and some/all of the layers are imported
                return {
                    title: translate(Content.map7.delete.multipleTitleMix),
                    description: translate(Content.map7.delete.multipleDescriptionMix),
                    extraInfo: translate(Content.map7.delete.importLayerHint),
                };
            case !isGroupSelected && importLayerStatus === "none":
                // Only layers are selected and no layers are imported
                return {
                    title: translate(Content.map7.delete.multipleLayersTitle),
                    description: translate(Content.map7.delete.multipleLayersDescription),
                };
            case !isGroupSelected && importLayerStatus === "some":
                // Only layers are selected and some of the layers are imported
                return {
                    title: translate(Content.map7.delete.multipleLayersTitle),
                    description: translate(Content.map7.delete.multipleLayersDescription),
                    extraInfo: translate(Content.map7.delete.importLayerHint),
                };
            case !isGroupSelected && importLayerStatus === "all":
                // Only layers are selected and all of the layers are imported
                return {
                    title: translate(Content.map7.delete.multipleImportLayersTitle),
                    description: translate(Content.map7.delete.importLayersDescription),
                };
            default:
                throw new Error(`Multiple nodes selected but message available for the combination: isGroupSelected: ${isGroupSelected}, importLayerStatus: ${importLayerStatus}`);
        }
    }

    switch (importLayerStatus) {
        case "none":
            // Single layer is selected and no layers are imported
            return {
                title: translate(Content.map7.delete.singleLayerTitle),
                description: translate(Content.map7.delete.singleLayerDescription),
            };
        case "all":
            // Single layer is selected and all layers are imported
            return {
                title: translate(Content.map7.delete.singleImportLayersTitle),
                description: translate(Content.map7.delete.importLayersDescription),
            };
        default:
            throw new Error(`Single node selected but message available for the combination: importLayerStatus: ${importLayerStatus}`);
    }
};

/** Checks if all, some or none of the nodes are imported */
const checkIfNodesAreImported = (nodes: Node[]): ImportLayerStatus => {
    const allLayersImported = nodes.every((node) => node.sourceId !== node.id);
    if (allLayersImported) {
        return "all";
    }
    const someLayersImported = nodes.some((node) => node.sourceId !== node.id);
    if (someLayersImported) {
        return "some";
    }
    return "none";
};

const MessageContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;
